import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { usePermissions, useTranslate } from "react-admin";
import { Navigate } from "react-router-dom";

import {
  CONTENT_PERIODS,
  contentAnalyticsPeriod,
  ContentPeriod,
  DEFAULT_CONTENT_PERIOD,
  formatContentDate,
} from "../analytics/contentAnalyticsFilters";
import { ActivityThumbnail } from "../components/ActivityThumbnail";
import { ResourceName } from "../constants/resources";
import {
  ContentAnalyticsMode,
  ContentAnalyticsReportDocument,
  ContentAnalyticsReportQuery,
  ContentAnalyticsReportQueryVariables,
} from "../generated/graphql";

const LABEL = "analytics.contentUsage";
const PAGE_SIZE = 20;
const THUMBNAIL_KINDS = new Set([
  "audio",
  "audioButtonGroup",
  "contentDocument",
  "externalApp",
  "gallery",
  "game",
  "richText",
  "text",
  "video",
  "website",
  "youtube",
  "moderationCard",
  "navigation",
]);
type Report = ContentAnalyticsReportQuery["contentAnalyticsReport"];
type Translate = ReturnType<typeof useTranslate>;
const exactCount = (count: string): string => BigInt(count).toLocaleString("de-DE");
const barWidth = (count: string, maximum: string): string =>
  BigInt(maximum) === BigInt(0) ? "0%" : `${Number((BigInt(count) * BigInt(10000)) / BigInt(maximum)) / 100}%`;
const kindLabel = (kind: string, t: Translate): string =>
  t(`${LABEL}.kinds.${kind}`, { _: t(`${LABEL}.kinds.unknown`) });
const subtypeLabel = (subtype: string | null | undefined, t: Translate): string =>
  subtype ? t(`${LABEL}.gameModels.${subtype}`, { _: t(`${LABEL}.unknownSubtype`) }) : t(`${LABEL}.unknownSubtype`);

const UsageBar = ({
  title,
  completed,
  unfinished,
  maximum,
}: {
  title: string;
  completed: string;
  unfinished: string;
  maximum: string;
}) => {
  const t = useTranslate();
  return (
    <Box
      role="img"
      aria-label={t(`${LABEL}.usageBar`, {
        title,
        completed: exactCount(completed),
        unfinished: exactCount(unfinished),
      })}
      sx={{
        display: "flex",
        height: 12,
        width: "100%",
        minWidth: 100,
        bgcolor: "action.hover",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: barWidth(completed, maximum), bgcolor: "primary.main" }} />
      <Box sx={{ width: barWidth(unfinished, maximum), bgcolor: "warning.main" }} />
    </Box>
  );
};

const Overview = ({ report, title }: { report: Report; title: string }) => {
  const t = useTranslate();
  const maximum = report.overview.reduce(
    (max, row) => (BigInt(row.sessionCount) > BigInt(max) ? row.sessionCount : max),
    "0"
  );
  return (
    <>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ borderLeft: 12, borderColor: "primary.main", pl: 1 }}>
          {t(`${LABEL}.completed`)}
        </Typography>
        <Typography variant="caption" sx={{ borderLeft: 12, borderColor: "warning.main", pl: 1 }}>
          {t(`${LABEL}.unfinished`)}
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small" aria-label={title} sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t(`${LABEL}.colKind`)}</TableCell>
              <TableCell sx={{ width: "35%" }}>{t(`${LABEL}.total`)}</TableCell>
              <TableCell align="right">{t(`${LABEL}.completed`)}</TableCell>
              <TableCell align="right">{t(`${LABEL}.unfinished`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.overview.map(row => (
              <TableRow key={row.contentKind}>
                <TableCell>{kindLabel(row.contentKind, t)}</TableCell>
                <TableCell>
                  <UsageBar
                    title={kindLabel(row.contentKind, t)}
                    completed={row.completedSessionCount}
                    unfinished={row.unfinishedSessionCount}
                    maximum={maximum}
                  />
                  <Typography variant="caption">{exactCount(row.sessionCount)}</Typography>
                </TableCell>
                <TableCell align="right">{exactCount(row.completedSessionCount)}</TableCell>
                <TableCell align="right">{exactCount(row.unfinishedSessionCount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const Ranking = ({ report, title, mostUsed }: { report: Report; title: string; mostUsed: boolean }) => {
  const t = useTranslate();
  const maximum = report.items.reduce(
    (max, row) => (BigInt(row.sessionCount) > BigInt(max) ? row.sessionCount : max),
    "0"
  );
  return (
    <TableContainer>
      <Table size="small" aria-label={title} sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t(`${LABEL}.colTitle`)}</TableCell>
            <TableCell>{t(`${LABEL}.colKind`)}</TableCell>
            <TableCell>{t(`${LABEL}.colSubtype`)}</TableCell>
            <TableCell align="right">{t(`${LABEL}.total`)}</TableCell>
            <TableCell align="right">{t(`${LABEL}.completed`)}</TableCell>
            <TableCell align="right">{t(`${LABEL}.unfinished`)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.items.map(item => (
            <TableRow key={item.contentId}>
              <TableCell sx={{ minWidth: 240 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ActivityThumbnail
                    thumbnailFilename={item.thumbnailFilename ?? null}
                    cmsContentKind={THUMBNAIL_KINDS.has(item.contentKind) ? item.contentKind : null}
                    width={56}
                    height={42}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{item.title}</Typography>
                    {mostUsed && (
                      <UsageBar
                        title={item.title}
                        completed={item.completedSessionCount}
                        unfinished={item.unfinishedSessionCount}
                        maximum={maximum}
                      />
                    )}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell>{kindLabel(item.contentKind, t)}</TableCell>
              <TableCell>
                {item.contentKind === "game"
                  ? subtypeLabel(item.contentSubtype, t)
                  : t(`${LABEL}.leastUsed.notApplicable`)}
              </TableCell>
              <TableCell align="right">{exactCount(item.sessionCount)}</TableCell>
              <TableCell align="right">{exactCount(item.completedSessionCount)}</TableCell>
              <TableCell align="right">{exactCount(item.unfinishedSessionCount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ContentCard = ({ mode }: { mode: ContentAnalyticsMode }) => {
  const t = useTranslate();
  const [period, setPeriod] = useState<ContentPeriod>(DEFAULT_CONTENT_PERIOD);
  const [contentKind, setContentKind] = useState("");
  const [contentSubtype, setContentSubtype] = useState("");
  const [page, setPage] = useState(0);
  const [today] = useState(() => new Date());
  const overview = mode === ContentAnalyticsMode.Overview;
  const mostUsed = mode === ContentAnalyticsMode.MostUsed;
  const dates = contentAnalyticsPeriod(period, today);
  const { data, previousData, loading, error, refetch } = useQuery<
    ContentAnalyticsReportQuery,
    ContentAnalyticsReportQueryVariables
  >(ContentAnalyticsReportDocument, {
    variables: {
      ...dates,
      mode,
      contentKind: contentKind || null,
      contentSubtype: contentSubtype || null,
      first: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const report = !loading && !error ? data?.contentAnalyticsReport : undefined;
  const options = (data ?? previousData)?.contentAnalyticsReport;
  const rankingLabel = mostUsed ? "mostUsed" : "leastUsed";
  const selectedType =
    contentKind === "game" && contentSubtype
      ? t(`${LABEL}.typeAndSubtype`, { type: kindLabel(contentKind, t), subtype: subtypeLabel(contentSubtype, t) })
      : kindLabel(contentKind, t);
  const title = overview
    ? t(`${LABEL}.kindTitle`)
    : contentKind
      ? t(`${LABEL}.${rankingLabel}ByType`, { type: selectedType })
      : t(`${LABEL}.${rankingLabel}Title`);
  const reset = () => {
    setPeriod(DEFAULT_CONTENT_PERIOD);
    setContentKind("");
    setContentSubtype("");
    setPage(0);
  };
  const empty = report && (overview ? report.overview.length === 0 : report.items.length === 0);
  return (
    <Paper
      component="section"
      aria-label={title}
      variant="outlined"
      sx={{ p: { xs: 2, md: 4 }, borderWidth: 2, borderRadius: 2, minWidth: 0 }}
    >
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t(`${LABEL}.${overview ? "overviewScope" : mostUsed ? "rankingScope" : "catalogueScope"}`)}
      </Typography>
      <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 3, minWidth: 0 }}>
        <Typography component="legend" variant="subtitle2" sx={{ mb: 1.5 }}>
          {t(`${LABEL}.filters`)}
        </Typography>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
          <TextField
            select
            SelectProps={{ native: true }}
            label={t(`${LABEL}.periodLabel`)}
            value={period}
            onChange={event => {
              setPeriod(event.target.value as ContentPeriod);
              setPage(0);
            }}
            sx={{ minWidth: 230 }}
          >
            {CONTENT_PERIODS.map(value => (
              <option key={value} value={value}>
                {t(`${LABEL}.periods.${value}`)}
              </option>
            ))}
          </TextField>
          {!overview && (
            <TextField
              select
              SelectProps={{ native: true }}
              label={t(`${LABEL}.leastUsed.filter`)}
              value={contentKind}
              onChange={event => {
                setContentKind(event.target.value);
                setContentSubtype("");
                setPage(0);
              }}
              sx={{ minWidth: 190 }}
            >
              <option value="">{t(`${LABEL}.leastUsed.allTypes`)}</option>
              {Array.from(new Set([...(options?.contentTypes ?? []), ...(contentKind ? [contentKind] : [])])).map(
                kind => (
                  <option key={kind} value={kind}>
                    {kindLabel(kind, t)}
                  </option>
                )
              )}
            </TextField>
          )}
          {!overview && contentKind === "game" && (
            <TextField
              select
              SelectProps={{ native: true }}
              label={t(`${LABEL}.colSubtype`)}
              value={contentSubtype}
              onChange={event => {
                setContentSubtype(event.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 190 }}
            >
              <option value="">{t(`${LABEL}.allSubtypes`)}</option>
              {Array.from(
                new Set([...(options?.contentSubtypes ?? []), ...(contentSubtype ? [contentSubtype] : [])])
              ).map(subtype => (
                <option key={subtype} value={subtype}>
                  {subtypeLabel(subtype, t)}
                </option>
              ))}
            </TextField>
          )}
          <Button onClick={reset}>{t(`${LABEL}.reset`)}</Button>
        </Stack>
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t(`${LABEL}.periodDates`, { from: formatContentDate(dates.dateFrom), to: formatContentDate(dates.dateTo) })}
      </Typography>
      {loading && <CircularProgress aria-label={t("ra.message.loading")} />}
      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => {
                void refetch().catch(() => undefined);
              }}
            >
              {t(`${LABEL}.retry`)}
            </Button>
          }
        >
          {t("analytics.loadError")}
        </Alert>
      )}
      {report && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {report.latestDataDate
              ? t(`${LABEL}.latestUsage`, { date: formatContentDate(report.latestDataDate) })
              : t(`${LABEL}.noRecordedUsage`)}
          </Typography>
          {!overview && !mostUsed && (
            <Typography sx={{ mb: 2 }}>
              {t(`${LABEL}.unusedSummary`, {
                unused: exactCount(String(report.unusedCount)),
                total: exactCount(String(report.totalCount)),
              })}
            </Typography>
          )}
          {empty ? (
            <Typography color="text.secondary">
              {t(`${LABEL}.${overview || mostUsed ? "empty" : "leastUsed.empty"}`)}
            </Typography>
          ) : overview ? (
            <Overview report={report} title={title} />
          ) : (
            <Ranking report={report} title={title} mostUsed={mostUsed} />
          )}
          {!overview && (
            <TablePagination
              component="div"
              count={report.totalCount}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              labelDisplayedRows={({ from, to, count }) => t(`${LABEL}.leastUsed.page`, { from, to, count })}
              getItemAriaLabel={type => t(`${LABEL}.leastUsed.${type === "previous" ? "previousPage" : "nextPage"}`)}
              sx={{ overflowX: "auto" }}
            />
          )}
        </>
      )}
    </Paper>
  );
};

export const AnalyticsContent = () => {
  const { permissions, isPending } = usePermissions();
  const t = useTranslate();
  if (isPending) return <CircularProgress aria-label={t("ra.message.loading")} />;
  if (!permissions?.includes(ResourceName.Analytics)) return <Navigate to="/" replace />;
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", maxWidth: 1280, boxSizing: "border-box", mx: "auto" }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t(`${LABEL}.pageTitle`)}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {t(`${LABEL}.independentFilters`)}
      </Typography>
      <Stack spacing={5}>
        <ContentCard mode={ContentAnalyticsMode.Overview} />
        <ContentCard mode={ContentAnalyticsMode.MostUsed} />
        <ContentCard mode={ContentAnalyticsMode.LeastUsed} />
      </Stack>
    </Box>
  );
};
